import React, { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function DocumentDownloader({ baseUrl, documentType, fileName }) {
  const [issueDate, setIssueDate] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    
    try {
      // 1. Prepare the URL with the custom date
      const encodedDate = encodeURIComponent(issueDate.trim());
      const separator = baseUrl.includes('?') ? '&' : '?';
      const endpoint = `${baseUrl}${separator}issue_date=${encodedDate}`;
      
      // 2. Attach the Backend URL and Auth Token
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const token = localStorage.getItem("admin_token");
      
      // 3. Securely fetch the PDF
      const response = await fetch(`${backendUrl}${endpoint}`, {
        method: "GET",
        headers: { 
          "Authorization": `Bearer ${token}` 
        }
      });

      if (!response.ok) {
        if (response.status === 404) throw new Error("File not found on server.");
        if (response.status === 401) throw new Error("Unauthorized. Please log in again.");
        throw new Error(`Download failed with status: ${response.status}`);
      }

      // 4. Convert the response to a Blob and force download
      const blob = await response.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      
      toast.success(`${documentType} downloaded successfully!`);
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 p-5 border border-stone-200 rounded-sm bg-white shadow-sm max-w-sm">
      <div>
        <h3 className="text-sm font-bold text-stone-800">
          Download {documentType}
        </h3>
        <p className="text-xs text-stone-500 mt-0.5">
          {fileName}
        </p>
      </div>
      
      <div className="mt-2">
        <label className="block text-[11px] font-semibold tracking-wider uppercase text-stone-500 mb-1.5">
          Issue Date (Optional)
        </label>
        <input 
          type="text"
          placeholder="e.g., 16 June 2026"
          value={issueDate}
          onChange={(e) => setIssueDate(e.target.value)}
          disabled={isDownloading}
          className="w-full text-sm font-mono px-3 py-2 border border-stone-300 rounded-sm focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 transition-colors disabled:opacity-50 disabled:bg-stone-50"
        />
        <p className="text-[10px] text-stone-400 mt-1">
          Leave blank to use the default printed layout.
        </p>
      </div>

      <button 
        onClick={handleDownload}
        disabled={isDownloading}
        className="mt-2 flex items-center justify-center gap-2 w-full bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 disabled:bg-indigo-400 text-white text-sm font-medium py-2 px-4 rounded-sm transition-colors"
      >
        {isDownloading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Download className="w-4 h-4" />
        )}
        {isDownloading ? "Generating..." : "Generate & Download"}
      </button>
    </div>
  );
}
